import { useEffect, useRef, useState } from 'react';

const CONTAINER_ID = 'brissbella-kiosk-qr-reader';

const extractAcademyId = (raw) => {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';

  const match = trimmed.match(/\/student-pass\/([^/?#]+)/i);
  if (match && match[1]) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }

  try {
    const url = new URL(trimmed);
    const segments = url.pathname.split('/').filter(Boolean);
    const idx = segments.findIndex((segment) => segment === 'student-pass');
    if (idx !== -1 && segments[idx + 1]) {
      return decodeURIComponent(segments[idx + 1]);
    }
  } catch {
    // not a URL, treat as raw id
  }

  return trimmed;
};

const QrScannerModal = ({ open, onClose, onResult }) => {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [manualValue, setManualValue] = useState('');
  const [torchOn, setTorchOn] = useState(false);
  const scannerRef = useRef(null);
  const onResultRef = useRef(onResult);
  const firedRef = useRef(false);

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    let startPromise = null;
    firedRef.current = false;
    setStatus('starting');
    setError('');
    setTorchOn(false);

    const stopScanner = async (scanner) => {
      if (!scanner) return;
      try {
        // Only stop if the scanner is actually running. html5-qrcode throws
        // "Cannot stop, scanner is not running or paused." when it isn't.
        const state = typeof scanner.getState === 'function'
          ? scanner.getState()
          : null;
        const isActive = state === 2 || state === 3; // SCANNING or PAUSED
        if (isActive) {
          await scanner.stop();
        }
      } catch (err) {
        // Swallow — best-effort cleanup.
        if (err?.message && !/not running/i.test(err.message)) {
          console.warn('QR scanner stop warning:', err);
        }
      }
      try {
        scanner.clear();
      } catch {
        /* noop */
      }
    };

    const start = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (cancelled) return null;

        const node = document.getElementById(CONTAINER_ID);
        if (!node) return null;

        const scanner = new Html5Qrcode(CONTAINER_ID, { verbose: false });
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: { ideal: 'environment' } },
          {
            fps: 10,
            qrbox: (vw, vh) => {
              const size = Math.min(vw, vh) * 0.7;
              return { width: size, height: size };
            },
            aspectRatio: 1,
          },
          (decodedText) => {
            if (firedRef.current) return;
            const academyStudentId = extractAcademyId(decodedText);
            if (!academyStudentId) return;
            firedRef.current = true;
            const handler = onResultRef.current;
            if (typeof handler === 'function') handler(academyStudentId);
          },
          () => {
            /* ignore per-frame decode errors */
          },
        );

        if (cancelled) {
          // Cleanup already requested while we were starting up; stop now.
          await stopScanner(scanner);
          return null;
        }

        setStatus('running');
        return scanner;
      } catch (err) {
        console.error('Failed to start QR scanner:', err);
        if (!cancelled) {
          setStatus('error');
          setError(
            err?.message ||
              'Camera not available. Allow camera permission or enter the student ID manually.',
          );
        }
        return null;
      }
    };

    startPromise = start();

    return () => {
      cancelled = true;
      const scanner = scannerRef.current;
      scannerRef.current = null;
      // Wait for the in-flight start() to finish (or fail) before stopping,
      // so we never call stop() while the scanner is mid-initialisation.
      Promise.resolve(startPromise)
        .catch(() => null)
        .then(() => stopScanner(scanner));
    };
  }, [open]);

  const handleToggleTorch = async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      const nextTorch = !torchOn;
      await scanner.applyVideoConstraints({
        advanced: [{ torch: nextTorch }],
      });
      setTorchOn(nextTorch);
    } catch (err) {
      console.warn('Torch not supported on this device:', err);
    }
  };

  const handleManualSubmit = (event) => {
    event.preventDefault();
    const academyStudentId = extractAcademyId(manualValue);
    if (!academyStudentId) return;
    if (firedRef.current) return;
    firedRef.current = true;
    const handler = onResultRef.current;
    if (typeof handler === 'function') handler(academyStudentId);
    setManualValue('');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-stone-950/95 text-white">
      <div className="flex items-center justify-between px-4 py-3">
        <div>
          <p className="text-sm font-semibold uppercase tracking-wide text-white/70">
            Kiosk scan
          </p>
          <p className="text-lg font-semibold">Point at a student QR</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleToggleTorch}
            className={`min-h-[44px] rounded-full border border-white/30 px-4 text-sm font-semibold transition ${
              torchOn ? 'bg-amber-400 text-stone-900' : 'bg-white/10 text-white'
            }`}
          >
            {torchOn ? 'Torch on' : 'Torch'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] min-w-[44px] rounded-full border border-white/30 bg-white/10 text-lg font-semibold text-white"
            aria-label="Close scanner"
          >
            ×
          </button>
        </div>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <div
          id={CONTAINER_ID}
          className="h-full w-full [&>video]:h-full [&>video]:w-full [&>video]:object-cover"
        />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-64 w-64 rounded-3xl border-[3px] border-white/70 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
        </div>
        {status === 'error' ? (
          <div className="absolute inset-x-4 bottom-28 rounded-2xl bg-red-500/90 px-4 py-3 text-sm">
            {error}
          </div>
        ) : null}
        {status === 'starting' ? (
          <div className="absolute inset-x-4 bottom-28 rounded-2xl bg-white/10 px-4 py-3 text-center text-sm">
            Starting camera...
          </div>
        ) : null}
      </div>

      <form
        onSubmit={handleManualSubmit}
        className="flex flex-col gap-2 bg-stone-900 px-4 py-4 pb-[env(safe-area-inset-bottom)]"
      >
        <label className="text-xs font-semibold uppercase tracking-wide text-white/60">
          Or enter student ID
        </label>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={manualValue}
            onChange={(event) => setManualValue(event.target.value)}
            placeholder="BRS-XXXXXX-XXXXXX"
            className="min-h-[52px] flex-1 rounded-2xl border border-white/20 bg-stone-950 px-4 text-base text-white placeholder:text-white/40 focus:border-white focus:outline-none"
            autoComplete="off"
          />
          <button
            type="submit"
            className="min-h-[52px] rounded-2xl bg-[#d61f74] px-5 text-sm font-semibold text-white"
          >
            Open
          </button>
        </div>
      </form>
    </div>
  );
};

export { extractAcademyId };
export default QrScannerModal;
